import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteLibraryItem, libraryKeys, type TLibraryItem } from '../../entities/library';
import { ApiError } from '../../shared/api/errors';
import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { RefusalNote } from '../../shared/ui/refusal-note';

interface IInUseFlow {
  flowId: string;
  flowName: string;
  where: 'live' | 'draft' | 'history';
  nodes: { key: string; name: string }[];
}

const WHERE: Record<IInUseFlow['where'], string> = {
  live: 'runs it',
  draft: 'in its draft',
  history: 'an older published version pins it',
};

// 409 `library.in_use` names the flows: each is a link, so fixing it is one click away.
const inUseFlows = (error: unknown): IInUseFlow[] => {
  if (!(error instanceof ApiError) || error.code !== 'library.in_use') return [];
  const details = error.rawDetails as { flows?: IInUseFlow[] } | undefined;
  return details?.flows ?? [];
};

export const LibraryDelete = ({
  item,
  onDeleted,
}: {
  item: TLibraryItem;
  onDeleted: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const client = useQueryClient();
  const remove = useMutation({
    mutationFn: () => deleteLibraryItem(item.id),
    onSuccess: () => {
      setOpen(false);
      void client.invalidateQueries({ queryKey: libraryKeys.all });
      onDeleted();
    },
  });
  const flows = inUseFlows(remove.error);

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Delete
      </Button>
      <ConfirmDelete
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) remove.reset();
        }}
        entity={item.kind === 'prompt' ? 'prompt' : 'code'}
        name={item.name}
        consequences={`its ${item.latestVersion === 1 ? 'one version' : `${item.latestVersion} versions`}`}
        waiting={remove.isPending}
        onConfirm={() => remove.mutate()}
        note={
          remove.isError ? (
            <div className="flex flex-col gap-2">
              <RefusalNote error={remove.error} />
              {flows.length > 0 && (
                <ul className="flex flex-col gap-1 text-meta">
                  {flows.map((flow) => (
                    <li key={`${flow.flowId}-${flow.where}`}>
                      <Link
                        to="/flows/$flowId"
                        params={{ flowId: flow.flowId }}
                        className="text-primary hover:underline"
                      >
                        {flow.flowName}
                      </Link>{' '}
                      <span className="text-muted">
                        · {WHERE[flow.where]}
                        {flow.nodes.length > 0
                          ? ` (${flow.nodes.map((node) => node.name).join(', ')})`
                          : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : undefined
        }
      />
    </>
  );
};
